import { createContext, useContext, useMemo, useRef, useState, useEffect } from "react";

type ToastKind = "success" | "error" | "info";
type Toast = { id: number; kind: ToastKind; text: string; ttl: number };

type ToastAPI = {
  show: (text: string, kind?: ToastKind, ttlMs?: number) => void;
  success: (text: string, ttlMs?: number) => void;
  error: (text: string, ttlMs?: number) => void;
  info: (text: string, ttlMs?: number) => void;
};

const ToastCtx = createContext<ToastAPI | null>(null);
// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const v = useContext(ToastCtx);
  if (!v) throw new Error("useToast must be used within <ToasterProvider>");
  return v;
};

export function ToasterProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(1);

  const api = useMemo<ToastAPI>(
    () => ({
      show: (text, kind = "info", ttlMs = 2600) => {
        const id = seq.current++;
        setItems((prev) => [...prev, { id, kind, text, ttl: Date.now() + ttlMs }]);
        setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), ttlMs + 100);
      },
      success: (t, ms) => api.show(t, "success", ms),
      error: (t, ms) => api.show(t, "error", ms),
      info: (t, ms) => api.show(t, "info", ms),
    }),
    []
  );

  // Inicializar globalToast
  useEffect(() => {
    import("../../lib/toast").then((module) => {
      module.setGlobalToast(api);
    });
  }, [api]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {/* contenedor visual */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            className={
              "shadow-lg rounded-xl px-4 py-3 text-sm text-white " +
              (t.kind === "success" ? "bg-teal-600" : t.kind === "error" ? "bg-rose-600" : "bg-slate-700")
            }
            role="status"
            aria-live="polite"
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
